"use client";

import {
  Box,
  BoxProps,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { LuFacebook, LuLinkedin, LuLink } from "react-icons/lu";
import { PiThreadsLogo } from "react-icons/pi";
import {
  FacebookShareButton,
  LinkedinShareButton,
  ThreadsShareButton,
} from "react-share";
import { Emoji } from "@/_components/ui/Emoji";
import { ShareIconButton } from "./ShareIconButton";
import {
  buildRankingDisplay,
  copyToClipboard,
  getAvailableRanking,
  getIndustryRankSegment,
  getMarkerPositionPercent,
  INDUSTRY_RANK_SEGMENTS,
  RANK_BAR_GRADIENT,
  RANKING_PALETTE,
} from "./_utils";

export interface IndustryRankingCardProps {
  /**
   * API `percentile` = Top % (lower = better).
   * Example: 6.6 → Top 6.6%, ahead of 93.4%.
   */
  percentile?: number | null;
  /** @deprecated Alias of `percentile` (Top %). Prefer `percentile`. */
  topPercent?: number | null;
  industry?: string | null;
  description?: string;
  shareUrl?: string;
  shareTitle?: string;
  showShare?: boolean;
}

const cardShellProps: BoxProps = {
  bg: "blackAlpha.300",
  border: "1px solid",
  borderColor: "whiteAlpha.100",
  rounded: "2xl",
  p: { base: 5, md: 6 },
  w: "full",
  shadow: "shadow-primary-xl",
};

export function IndustryRankingCard({
  percentile,
  topPercent,
  industry,
  description,
  shareUrl,
  shareTitle,
  showShare = true,
}: IndustryRankingCardProps) {
  const t = useTranslations("resume_benchmark.industry_ranking");

  // API percentile = Top % (lower = better). Prefer `percentile`.
  const topPercentInput =
    typeof percentile === "number" && Number.isFinite(percentile)
      ? percentile
      : typeof topPercent === "number" && Number.isFinite(topPercent)
        ? topPercent
        : null;

  const ranking = getAvailableRanking(topPercentInput, industry);

  if (!ranking) {
    return (
      <Box {...cardShellProps}>
        <Text
          textStyle="2xs"
          fontWeight="semibold"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color="neutral.400"
          mb={3}
          fontFamily="mono"
        >
          {t("heading")}
        </Text>
        <Text
          textStyle={{ base: "2xl", md: "3xl" }}
          fontWeight="bold"
          color="neutral.100"
          fontFamily="spaceGrotesk"
          mb={2}
        >
          {t("unavailable_title")}
        </Text>
        <Text
          textStyle="sm"
          color="neutral.400"
          lineHeight="1.7"
          fontFamily="spaceGrotesk"
        >
          {t("unavailable_description")}
        </Text>
      </Box>
    );
  }

  const { topPercent: resolvedTopPercent, industry: resolvedIndustry } =
    ranking;
  const display = buildRankingDisplay(resolvedTopPercent);
  const segment = getIndustryRankSegment(display.topPercent);
  const markerLeft = getMarkerPositionPercent(display.topPercent);

  const resolvedUrl =
    shareUrl ??
    (typeof window !== "undefined"
      ? window.location.href
      : "https://www.knoot.ai");
  const resolvedTitle =
    shareTitle ??
    (display.showTopPercent
      ? t("share_title", {
          percent: display.topPercentLabel,
          industry: resolvedIndustry,
        })
      : t("share_title_completed", {
          percent: display.passedPercentLabel,
          industry: resolvedIndustry,
        }));

  return (
    <Box {...cardShellProps}>
      <Flex
        justify="space-between"
        align="flex-start"
        gap={4}
        mb={4}
        flexWrap="wrap"
      >
        <VStack align="flex-start" gap={1} flex={1} minW={0}>
          <Text
            textStyle={{ base: "xs", md: "2xs" }}
            fontWeight="semibold"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="neutral.400"
            fontFamily="mono"
          >
            {t("heading")}
          </Text>

          {display.showTopPercent ? (
            <HStack
              align="baseline"
              gap={3}
              flexWrap="wrap"
              mt={{ base: 3, md: 1 }}
            >
              <Text
                textStyle="4xl"
                fontWeight="bold"
                color="primary.50"
                lineHeight="1.1"
                fontFamily="spaceGrotesk"
              >
                {t("top_percent", { percent: display.topPercentLabel })}
              </Text>
              <Text textStyle="sm" color="neutral.300" maxW="360px">
                {t("surpassed", { percent: display.passedPercentLabel })}
              </Text>
            </HStack>
          ) : (
            <VStack align="flex-start" gap={1} mt={{ base: 3, md: 1 }}>
              <Text
                textStyle={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                color="primary.50"
                lineHeight="1.15"
                fontFamily="spaceGrotesk"
              >
                {t("completed_title", {
                  percent: display.passedPercentLabel,
                })}
              </Text>
              {/* <Text textStyle="sm" color="neutral.300" maxW="420px">
                {t("completed_subtitle")}
              </Text> */}
            </VStack>
          )}
        </VStack>

        {/* Ranking badge */}
        <HStack
          gap={1.5}
          px={3}
          py={1.5}
          align="center"
          rounded="full"
          bg={segment.badgeBg}
          border="1px solid"
          borderColor={segment.badgeBorder}
          flexShrink={0}
          id="industry-rank-badge"
          display={{ base: "none", md: "flex" }}
        >
          <Emoji emoji={segment.emoji} size={{ base: 3, md: 5 }} />
          <Text
            textStyle={{ base: "2xs", md: "sm" }}
            fontWeight="semibold"
            color={segment.badgeFg}
          >
            {t(`ranks.${segment.rank}`)}
          </Text>
        </HStack>
      </Flex>

      <Text textStyle="sm" color="neutral.200" lineHeight="1.7" mb={3}>
        {description ??
          (display.showTopPercent
            ? t.rich("description_outperforming", {
                percent: display.topPercentLabel,
                industry: resolvedIndustry,
                boldIndustry: (chunks) => (
                  <Text
                    as="span"
                    fontWeight="bold"
                    fontSize="md"
                    color="neutral.50"
                  >
                    {chunks}
                  </Text>
                ),
              })
            : t.rich("description_completed", {
                percent: display.passedPercentLabel,
                industry: resolvedIndustry,
                boldIndustry: (chunks) => (
                  <Text
                    as="span"
                    fontWeight="bold"
                    fontSize="md"
                    color="neutral.50"
                  >
                    {chunks}
                  </Text>
                ),
              }))}
      </Text>

      <Box position="relative" pt={10} mb={2}>
        <Box
          position="absolute"
          top={0}
          left={`${markerLeft}%`}
          transform="translateX(-50%)"
          display="flex"
          flexDirection="column"
          alignItems="center"
          zIndex={2}
          maxW="140px"
        >
          <Box
            px={2.5}
            py={1}
            rounded="md"
            bg={RANKING_PALETTE.signalViolet}
            mb={1}
          >
            <Text
              textStyle="2xs"
              fontWeight="semibold"
              color={RANKING_PALETTE.cloud}
              whiteSpace="nowrap"
              fontFamily="mono"
            >
              {t("your_position")} · {display.passedPercentLabel} %
            </Text>
          </Box>
          <Box
            w="1.5px"
            h={5}
            bg={RANKING_PALETTE.signalViolet}
            opacity={0.95}
          />
        </Box>

        <Box
          h={3}
          rounded="full"
          overflow="hidden"
          w="full"
          position="relative"
          backgroundImage={RANK_BAR_GRADIENT}
        />
      </Box>

      <Flex w="full" mb={6} gap={0}>
        {INDUSTRY_RANK_SEGMENTS.map((s) => (
          <HStack
            key={s.rank}
            flexBasis={`${s.widthPercent}%`}
            flexGrow={0}
            flexShrink={0}
            gap={1}
            align="center"
            justify="flex-start"
            px={0.5}
            overflow="hidden"
            display={{ base: "none", md: "flex" }}
          >
            <Emoji emoji={s.emoji} size={{ base: 3, md: 4 }} />
            <Text
              textStyle="2xs"
              color={RANKING_PALETTE.fogLight}
              fontFamily="mono"
              truncate
              hideBelow={"md"}
            >
              {t(`segments.${s.rank}`)}
            </Text>
          </HStack>
        ))}
      </Flex>

      {showShare ? (
        <HStack gap={3} align="center">
          <HStack gap={2}>
            <ShareIconButton aria-label={t("share_facebook_aria")}>
              <FacebookShareButton url={resolvedUrl} title={resolvedTitle}>
                <Icon as={LuFacebook} boxSize={4} color="neutral.300" />
              </FacebookShareButton>
            </ShareIconButton>
            <ShareIconButton aria-label={t("share_linkedin_aria")}>
              <LinkedinShareButton url={resolvedUrl} title={resolvedTitle}>
                <Icon as={LuLinkedin} boxSize={4} color="neutral.300" />
              </LinkedinShareButton>
            </ShareIconButton>
            <ShareIconButton aria-label={t("share_threads_aria")}>
              <ThreadsShareButton url={resolvedUrl} title={resolvedTitle}>
                <Icon as={PiThreadsLogo} boxSize={4} color="neutral.300" />
              </ThreadsShareButton>
            </ShareIconButton>
            <ShareIconButton
              aria-label={t("copy_link_aria")}
              onClick={() =>
                copyToClipboard(resolvedUrl, {
                  success: t("link_copied"),
                  error: t("copy_failed"),
                })
              }
            >
              <Icon as={LuLink} boxSize={4} color="inherit" />
            </ShareIconButton>
          </HStack>
        </HStack>
      ) : null}
    </Box>
  );
}
